import React, {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useHistory, useParams } from 'react-router';

import { useAppSelector } from '@common/hooks';
import { useRequest } from '@common/services/api';
import { User, UserStatus } from '@common/types/objects';

import { selected } from '@modules/organisation/selectors/organisation';
import * as userSelector from '@modules/core/selectors/logged-user';
import * as networkSelector from '@modules/network/selectors/network';

import {
  APIResponseConversation,
  APIResponseFetchConversations,
  APIResponsePrivateMessage,
  FullConversation,
  PrivateMessage,
  ESearhFilterTypes,
  EConversationFilterTypes
} from '../types/objects';
import {
  parseAndSortConversationFromMessage,
  parseAndSortConversations,
  parseConversation,
  sortConversations,
} from '../utils/conversations';
import { createActivity } from '../utils/activity';

import { EConversationActivityTypes, RouteParams } from '../definitions';
import { ConversationFormPayload } from '../forms/conversation/conversation';

import fetchConversation from '../api/fetch-conversation';
import { fetchMessages } from '../api/fetch-messages';
import { toggleNotifications } from '../api/toggle-notifications';
import { toggleArchive } from '../api/toggle-archive';
import { removeConversation } from '../api/remove-conversation';
import { updateAdmins } from '../api/update-admins';
import { leaveConversation } from '../api/leave-conversation';
import { togglePin } from '../api/toggle-pin';
import { toggleMarkAsRead } from '../api/toggle-markasread';
import { toggleFavourite } from '../api/toggle-favourite';
import { toggleMute } from '../api/toggle-mute';

import { usePusherUserStateListener } from '../hooks/use-pusher-user-state-listener';
import { usePusherConversationListener } from '../hooks/use-pusher-conversation-listener';
import * as Alert from '@common/services/alert';
import { useTranslation } from 'react-i18next';
import { PostMessageFormData } from '../forms/post-message/post-message';
import { postMessage } from '@modules/chat/api/post-message';
import { parseMessages } from '../utils/messages';

export type ChatFilterType = 'all' | 'unread' | 'favorites' | 'groups';

interface ChatStore {
  conversations: FullConversation[];
  setConversations: Dispatch<SetStateAction<FullConversation[]>>;
  loading: boolean;
  refetch: () => void;
  reset: () => void;
  meta: APIResponseFetchConversations['meta'];
  conversationId: string | undefined;
  getPath: (conversationId?: string) => string;
  usersStatus: Record<string, UserStatus | null>;
  currentConversation: FullConversation | null;
  setCurrentConversation: Dispatch<SetStateAction<FullConversation | null>>;
  messages: PrivateMessage[];
  setMessages: Dispatch<SetStateAction<PrivateMessage[]>>;
  handleNewMessage: (id: string, message: PrivateMessage) => Promise<void>;
  handleOpenProfile: (id: string) => void;
  handleToggleNotifications: (id: string, mute: boolean) => Promise<void>;
  handleToggleArchive: (id: string, shouldArchive: boolean) => Promise<void>;
  handleRemoveConversation: (id: string) => Promise<void>;
  handleEditGroupAdmin: (id: string, user: User, add: boolean) => Promise<void>;
  handleEditParticipants: (
    id: string,
    activities: PrivateMessage | PrivateMessage[],
    participants: User | User[]
  ) => Promise<void>;
  handleLeave: (id: string) => Promise<void>;
  handleUpdateConversation: (
    id: string,
    data: ConversationFormPayload
  ) => Promise<void>;
  handleUpdateLastMessage: (
    id: string,
    propName: keyof PrivateMessage,
    value: PrivateMessage[keyof PrivateMessage],
    messageId: string
  ) => Promise<void>;
  isNewConversationModalVisible: boolean;
  setNewConversationModalVisible: (visible: boolean) => void;
  setSearch: (search: string) => void;
  search: string;
  searchFilter: ESearhFilterTypes;
  selectedFilter: ChatFilterType;
  handleConversationPinOrUnpin: (id: string, isPinned: boolean) => Promise<void>;
  handleConversationMarkAsReadOrUnread: (id: string, isRead: boolean) => Promise<void>;
  handleConversationFavouriteOrUnfavourite: (id: string, isFavourite: boolean) => Promise<void>;
  handleConversationMuteOrUnmute: (id: string, isMuted: boolean) => Promise<void>;
  handleNewConversation: (conversation: FullConversation) => void;
  handleOpenConversation: (conversation: FullConversation) => void;
  handleRetryMessage: (message: PrivateMessage) => Promise<void>;
  selectedSearchIndex: number | undefined;
  setSelectedSearchIndex: (index: number | undefined) => void;
  isSearchFocused: boolean;
  setIsSearchFocused: (focused: boolean) => void;
  onSearchFocus: () => void;
  onSearchBlur: () => void;
  onSearchCancel: () => void;
  handleSearchChange: (value: string | null) => void;
  abortFetchConversations: () => void;
}

const ChatContext = createContext<ChatStore | null>(null);

export const ChatStoreProvider = ({
  children,
  basePath,
}: PropsWithChildren<{ basePath: string }>) => {
  const history = useHistory();
  const orgId = useAppSelector(selected).id;
  const { t } = useTranslation();

  const loggedUser = useAppSelector(userSelector.selected);
  const network = useAppSelector(networkSelector.selected);

  const { filter, conversationId } = useParams<RouteParams>();

  const [selectedSearchIndex, setSelectedSearchIndex] = useState<number | undefined>(undefined);
  const [currentConversation, setCurrentConversation] =
    useState<FullConversation | null>(null);
  const [messages, setMessages] = useState<PrivateMessage[]>([]);
  const [isNewConversationModalVisible, setNewConversationModalVisible] =
    useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  const selectedFilter = useMemo(() => {
    return filter || EConversationFilterTypes.ALL;
  }, [filter]);

  const setSearch = useCallback((value: string) => {
    if (value) {
      history.push(`${history.location.pathname}?search=${value}`);
    } else {
      history.push(history.location.pathname);
    }
  }, [history]);

  const search = useMemo(() => {
    return history.location.search.split('?')[1]?.split('=')[1] || '';
  }, [history.location.search]);

  const searchFilter = useMemo(() => {
    return filter === 'messages' ? ESearhFilterTypes.MESSAGES : ESearhFilterTypes.CHATS;
  }, [filter]);

  const payload = useMemo(() => {
    const isMessageSearch = searchFilter === ESearhFilterTypes.MESSAGES;

    if (isMessageSearch) {
      return { q: search };
    }

    return {
      archived: filter === 'archived',
      q: search,
      include: 'participants',
      filter: selectedFilter !== 'all' && selectedFilter !== 'chats' && selectedFilter !== 'messages' ?
        selectedFilter : undefined,
    };
  }, [filter, search, searchFilter, selectedFilter]);

  const apiConfig = useMemo(() => ({
    useCursor: true,
    limit: 20,
    payload,
    transform: (res: APIResponseFetchConversations) => {
      const isMessageSearch = searchFilter === ESearhFilterTypes.MESSAGES;
      const sorted = isMessageSearch
        ? parseAndSortConversationFromMessage(
          res.data as unknown as APIResponsePrivateMessage[],
          res.meta.related,
          loggedUser.id,
          search
        )
        : parseAndSortConversations(
          res.data as APIResponseConversation[],
          res.meta.related,
          loggedUser.id
        );

      return {
        ...res,
        data: sorted,
      };
    },
    cachePolicy: 'no-cache',
  }), [payload, loggedUser.id, search, searchFilter]);

  const apiUrl = useMemo(() => {
    const baseUrl = `/v3/organisations/${orgId}/conversations`;
    return searchFilter === ESearhFilterTypes.MESSAGES
      ? `${baseUrl}/messages/search`
      : baseUrl;
  }, [orgId, searchFilter]);

  const {
    data: conversations,
    meta,
    setData: setConversations,
    loading,
    refetch,
    reset,
    abort: abortFetchConversations,
  } = useRequest(apiUrl, apiConfig);

  useEffect(() => {
    if (search) {
      setIsSearchFocused(true);
    }
  }, [search]);

  useEffect(() => {
    if (isSearchFocused) {
      if (search !== '') {
        reset();
      }
    } else {
      reset();
    }
  }, [payload, reset, search, isSearchFocused]);

  const getPath = useCallback((convId?: string) => {
    return filter
      ? `${basePath}/filter/${filter}${convId ? `/${convId}` : ''}`
      : `${basePath}${convId ? `/${convId}` : ''}`;
  }, [filter, basePath]);

  // When the conversation is not in the list, fetch it and add it to the list
  const addNewConversation = useCallback(
    async (id: string): Promise<FullConversation | null> => {
      try {
        const { data, meta: fetchMeta } = await fetchConversation(orgId, id);
        const related = fetchMeta.related || {};
        const relatedObj = {
          users: (related as any).users || [],
          conversations: (related as any).conversations || [],
        };
        const parsedConversation = parseConversation(data, relatedObj, loggedUser.id);
        setConversations((prev) => sortConversations([
          parsedConversation,
          ...(prev || []),
        ]));
        setCurrentConversation(parsedConversation);
        return parsedConversation;
      } catch (error) {
        return null;
      }
    },
    [orgId, loggedUser.id, setConversations]
  );

  // Open the conversation when the conversationId is changed
  useEffect(() => {
    if (!conversations || !conversations.length) return;
    if (conversationId && currentConversation?.id !== conversationId) {
      const conversation = conversations.find((c) => c.id === conversationId);
      if (conversation) {
        setCurrentConversation(conversation as FullConversation);
      } else {
        addNewConversation(conversationId).then((fetchedConversation) => {
          if (fetchedConversation && conversations.length > 0 &&
            ((filter === 'archived' && !fetchedConversation.is_archived) ||
              (filter !== 'archived' && fetchedConversation.is_archived))) {
            history.push(getPath(conversations[0].id));
          }
        });
      }
    }
  }, [
    conversationId,
    conversations,
    currentConversation?.id,
    setCurrentConversation,
    addNewConversation,
    filter,
    history,
    getPath
  ]);

  // Conversation mutations
  const updateConversation = useCallback((
    id: string,
    propName: keyof FullConversation,
    value: FullConversation[keyof FullConversation] | ((prev: any) => FullConversation[keyof FullConversation]),
  ) => {
    const updateAt = new Date().toISOString();
    setConversations((prev) => (prev && sortConversations(prev.map((conversation) => {
      if (conversation.id === id) {
        return {
          ...conversation,
          [propName]: value instanceof Function ? value(conversation[propName]) : value,
          updated_at: updateAt,
        };
      }
      return conversation;
    }))) || null);
    if (id === currentConversation?.id) {
      setCurrentConversation((prev) => (prev && {
        ...prev,
        [propName]: value instanceof Function ? value(prev[propName]) : value,
        updated_at: updateAt,
      }));
    }
  }, [setConversations, currentConversation]);

  // Remove conversation from list
  const removeConversationFromList = useCallback((
    id: string,
  ) => {
    setConversations((prev) => (prev && sortConversations(prev.filter((conversation) => {
      return conversation.id !== id;
    }))) || null);
    if (id === currentConversation?.id) {
      const nextConversation = conversations?.filter((c) => c.id !== id);
      const nextConversationIndex = nextConversation?.findIndex((c) => c.id === currentConversation?.id);
      if (nextConversation && nextConversationIndex !== -1 && nextConversation.length > 0) {
        setCurrentConversation(nextConversation[nextConversationIndex]);
      } else {
        setCurrentConversation(null);
      }
    }
  }, [setConversations, currentConversation, conversations]);

  const handleNewMessage = useCallback(
    async (id: string, message: PrivateMessage) => {
      if (conversations?.find((c) => c.id === id)) {
        updateConversation(id, 'last_message', message);
        // Only update the messages if the current conversation is the one that changed
        if (currentConversation?.id === id) {
          setMessages((prev) => [...(prev || []), message]);
          if (!message.seen) {
            // To mark the conversation as seen, we need to fetch the messages
            await fetchMessages(orgId, id, true);
            updateConversation(id, 'last_message', (prev) => ({
              ...prev,
              seen: true,
            }));
          }
        }
      } else {
        addNewConversation(id);
      }
    },
    [
      conversations,
      currentConversation?.id,
      updateConversation,
      setMessages,
      addNewConversation,
    ]
  );

  const chatParticipants = useMemo(() => currentConversation?.participants.reduce((acc, participant) => ({
    ...acc,
    [participant.id]: participant,
  }), {}), [currentConversation]);

  const handleRetryMessage = useCallback(async (message: PrivateMessage) => {
    if (!currentConversation) return;
    const retryPayload: PostMessageFormData = {
      text: message.text,
      attachments: message.attachments,
      message_id: null,
    };
    const newMessage = await postMessage(orgId, currentConversation.id, retryPayload);
    // @ts-expect-error
    const parsedMessage = parseMessages(newMessage, { users: Object.values(chatParticipants) })[0];
    setMessages((prev) => prev.filter((m) => m.id !== message.id));
    handleNewMessage(currentConversation.id, parsedMessage);
  }, [messages, currentConversation, updateConversation, chatParticipants]);

  // Opening profile
  const handleOpenProfile = useCallback(
    (id: string) => {
      const path = basePath.includes('/admin')
        ? `/admin/users/${id}`
        : `/networks/${network.id}/users/${id}`;
      history.push(path);
    },
    [basePath, history, network?.id]
  );

  const handleToggleNotifications = useCallback(
    async (id: string, mute: boolean) => {
      await toggleNotifications(orgId, id, mute);
      updateConversation(id, 'is_muted', mute);
    },
    [orgId, updateConversation]
  );

  const openNextConversation = useCallback(
    (id: string) => {
      const nextConversations = conversations?.filter((c) => c.id !== id);
      if (nextConversations && nextConversations.length > 0) {
        history.push(getPath(nextConversations[0].id));
      } else {
        history.push(getPath(undefined));
        setCurrentConversation(null);
      }
    },
    [conversations, getPath, history, setCurrentConversation]
  );

  const handleToggleArchive = useCallback(
    async (id: string, shouldArchive: boolean) => {
      await toggleArchive(orgId, id, shouldArchive);
      updateConversation(id, 'is_archived', shouldArchive);
      setConversations(
        (prev) => prev?.filter((conversation) => conversation.id !== id) || null
      );
      if (currentConversation?.id === id) openNextConversation(id);
    },
    [
      orgId,
      currentConversation?.id,
      updateConversation,
      setConversations,
      openNextConversation,
    ]
  );

  const handleRemoveConversation = useCallback(
    async (id: string) => {
      await removeConversation(orgId, id);
      setConversations(
        (prev) => prev?.filter((conversation) => conversation.id !== id) || null
      );
      if (currentConversation?.id === id) openNextConversation(id);
    },
    [orgId, currentConversation?.id, setConversations, openNextConversation]
  );

  const handleEditGroupAdmin = useCallback(async (id: string, user: User, add: boolean) => {
    await updateAdmins(orgId, id, [user.id], add);
    if (add) {
      updateConversation(id, 'admin_ids', (prev: string[]) => [...(prev || []), user.id]);
      updateConversation(id, 'admins', (prev: User[]) => [...(prev || []), user]);
    } else {
      updateConversation(id, 'admin_ids', (prev: string[]) => prev?.filter((admin) => admin !== user.id) || null);
      updateConversation(id, 'admins', (prev: User[]) => prev?.filter((admin) => admin.id !== user.id) || null);
    }
  }, [orgId, updateConversation]);

  const handleEditParticipants = useCallback(async (
    id: string, activities: PrivateMessage | PrivateMessage[], participants: User | User[],
  ) => {
    if (Array.isArray(activities)) {
      setMessages((prev) => [...(prev || []), ...activities.slice(0, -1)]);
      // Only last activity should be set as last_message on conversation
      await handleNewMessage(id, activities[activities.length - 1]);
    } else {
      await handleNewMessage(id, activities);
    }

    if (Array.isArray(participants)) {
      await updateConversation(id, 'participant_ids', (prev: string[]) => [...(prev || []), ...participants.map((p) => p.id)]);
      await updateConversation(id, 'participants', (prev: User[]) => [...(prev || []), ...participants]);
    } else {
      await updateConversation(id, 'participant_ids', (prev: string[]) => prev?.filter((p) => p !== participants.id) || null);
      await updateConversation(id, 'participants', (prev: User[]) => prev?.filter((p) => p.id !== participants.id) || null);
      await updateConversation(id, 'admins', (prev: User[]) => prev?.filter((p) => p.id !== participants.id) || null);
      await updateConversation(id, 'admin_ids', (prev: string[]) => prev?.filter((p) => p !== participants.id) || null);
    }
  }, [updateConversation, handleNewMessage, setMessages]);

  const handleLeave = useCallback(
    async (id: string) => {
      await leaveConversation(orgId, id);
      updateConversation(id, 'has_left', true);
      handleNewMessage(
        id,
        createActivity(EConversationActivityTypes.USER_LEFT, {
          user_id: loggedUser.id,
        })
      );
    },
    [orgId, updateConversation, handleNewMessage, loggedUser.id]
  );

  const handleUpdateConversation = useCallback(
    async (id: string, data: ConversationFormPayload) => {
      if (data.description)
        updateConversation(id, 'description', data.description);
      if (data.name) {
        updateConversation(id, 'name', data.name);
        handleNewMessage(
          id,
          createActivity(EConversationActivityTypes.CHANGED_NAME, {
            actor_id: loggedUser.id,
            new_name: data.name,
          })
        );
      }
      if (data.group_img) updateConversation(id, 'group_img', data.group_img);
    },
    [updateConversation, handleNewMessage]
  );

  const handleUpdateLastMessage = useCallback(
    async (
      id: string,
      propName: keyof PrivateMessage,
      value: PrivateMessage[keyof PrivateMessage],
      messageId: string
    ) => {
      const conversation = conversations?.find((c) => c.id === id);
      if (conversation?.last_message?.id === messageId) {
        updateConversation(id, 'last_message', (prev) => ({
          ...prev,
          [propName]: value,
        }));
      }
    },
    [conversations, updateConversation]
  );

  const handleConversationPinOrUnpin = useCallback(
    async (id: string, shouldPin: boolean) => {
      try {
        await togglePin(orgId, id, shouldPin);
        updateConversation(id, 'is_pinned', shouldPin);
      } catch (error: any) {
        Alert.error(error?.detail || t('chat:error_pinning_conversation'));
      }
    },
    [
      orgId,
      updateConversation,
    ]
  );

  const handleConversationMarkAsReadOrUnread = useCallback(
    async (id: string, shouldMarkAsRead: boolean) => {
      await toggleMarkAsRead(orgId, id, shouldMarkAsRead);
      // @ts-expect-error
      if (selectedFilter === EConversationFilterTypes.UNREAD) {
        removeConversationFromList(id);
      } else {
        updateConversation(id, 'last_message', (prev) => {
          return {
            ...prev,
            seen: shouldMarkAsRead,
            is_default_seen: !shouldMarkAsRead,
          };
        });
      }
    },
    [
      orgId,
      selectedFilter,
      updateConversation,
      removeConversationFromList,
    ]
  );

  const handleConversationFavouriteOrUnfavourite = useCallback(
    async (id: string, shouldFavourite: boolean) => {
      try {
        await toggleFavourite(orgId, id, shouldFavourite);
        updateConversation(id, 'is_favorite', shouldFavourite);
        if (selectedFilter === 'favorites') removeConversationFromList(id);
        Alert.success(shouldFavourite ? t('chat:conversation_favourited') : t('chat:conversation_unfavourited'));
      } catch (error: any) {
        Alert.error(error?.detail || t('chat:error_favouriting_conversation'));
      }
    },
    [
      orgId,
      selectedFilter,
      removeConversationFromList,
    ]
  );

  const handleConversationMuteOrUnmute = useCallback(
    async (id: string, shouldMute: boolean) => {
      await toggleMute(orgId, id, shouldMute);
      updateConversation(id, 'is_muted', shouldMute);
    },
    [
      orgId,
      updateConversation,
    ]
  );

  const handleNewConversation = useCallback((conversation: FullConversation) => {
    const alreadyExists = conversations?.find((c) => c.id === conversation.id);
    if (!alreadyExists) {
      setConversations((prev) => sortConversations([conversation, ...prev]));
    }
  }, [conversations]);

  const handleOpenConversation = useCallback((conversation: FullConversation) => {
    if (conversation.last_message?.id) {
      updateConversation(conversation.id, 'last_message', (prev) => ({
        ...prev,
        seen: true,
        is_default_seen: false,
      }));
    }
  }, [updateConversation]);

  usePusherConversationListener({
    orgId,
    loggedUserId: loggedUser.id,
    handleNewMessage,
    handleNewConversation,
  });

  // Socket listener users state
  const usersStatus = usePusherUserStateListener(conversations as FullConversation[]);

  // Update the onSearchFocus and onSearchBlur functions
  const onSearchFocus = useCallback(() => {
    setIsSearchFocused(true);
  }, []);

  const onSearchBlur = useCallback(() => {
    if (!search) {
      setIsSearchFocused(false);
    }
  }, [search]);

  const onSearchCancel = useCallback(() => {
    setIsSearchFocused(false);
    setSearch('');
  }, [setSearch]);

  const handleSearchChange = useCallback((value: string | null) => {
    setSearch(value || '');
    setIsSearchFocused(!!value);
  }, [setSearch]);

  // Add to contextValue
  const contextValue = useMemo(() => ({
    conversations: conversations as FullConversation[],
    setConversations: setConversations as unknown as Dispatch<SetStateAction<FullConversation[]>>,
    loading,
    refetch,
    reset,
    conversationId,
    getPath,
    meta,
    currentConversation,
    setCurrentConversation,
    usersStatus,
    messages,
    setMessages,
    handleNewMessage,
    handleOpenProfile,
    handleToggleNotifications,
    handleToggleArchive,
    handleRemoveConversation,
    handleEditGroupAdmin,
    handleEditParticipants,
    handleLeave,
    handleUpdateConversation,
    handleUpdateLastMessage,
    handleConversationPinOrUnpin,
    handleConversationMarkAsReadOrUnread,
    handleConversationFavouriteOrUnfavourite,
    handleConversationMuteOrUnmute,
    handleNewConversation,
    handleOpenConversation,
    handleRetryMessage,
    isNewConversationModalVisible,
    setNewConversationModalVisible,
    setSearch,
    search,
    selectedFilter,
    searchFilter: searchFilter as ESearhFilterTypes,
    selectedSearchIndex,
    setSelectedSearchIndex,
    isSearchFocused,
    setIsSearchFocused,
    onSearchFocus,
    onSearchBlur,
    onSearchCancel,
    handleSearchChange,
    abortFetchConversations,
  }), [
    conversations,
    setConversations,
    loading,
    refetch,
    reset,
    conversationId,
    getPath,
    meta,
    currentConversation,
    setCurrentConversation,
    usersStatus,
    messages,
    setMessages,
    handleNewMessage,
    handleOpenProfile,
    handleToggleNotifications,
    handleToggleArchive,
    handleRemoveConversation,
    handleEditGroupAdmin,
    handleEditParticipants,
    handleLeave,
    handleUpdateConversation,
    handleUpdateLastMessage,
    handleConversationPinOrUnpin,
    handleConversationMarkAsReadOrUnread,
    handleConversationFavouriteOrUnfavourite,
    handleConversationMuteOrUnmute,
    handleNewConversation,
    handleOpenConversation,
    handleRetryMessage,
    isNewConversationModalVisible,
    setNewConversationModalVisible,
    setSearch,
    search,
    selectedFilter,
    searchFilter,
    selectedSearchIndex,
    setSelectedSearchIndex,
    isSearchFocused,
    setIsSearchFocused,
    onSearchFocus,
    onSearchBlur,
    onSearchCancel,
    handleSearchChange,
    abortFetchConversations,
  ]);

  return (
    <ChatContext.Provider value={contextValue as ChatStore}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChatStore = (): ChatStore => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChatStore must be used within a ChatStoreProvider');
  }
  return context;
};

export const withChatStore = (Component: React.ComponentType<any>) => (props: any) => (
  <ChatStoreProvider basePath={props.basePath}>
    <Component {...props} />
  </ChatStoreProvider>
);
